import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Td,
  Text,
  Tr,
} from '@chakra-ui/react';
import { CaretDown, CaretRight } from 'phosphor-react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as GraficoTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { GlassCard } from '../../../../../components/glass';
import { COR } from '../regras';
import { barrasDaCascata, IPasso, num, numCurto } from '../regrasDoDetalhe';

export function Pilula({ texto, cor, suave }: { texto: string; cor: string; suave: string }) {
  return (
    <Box
      as="span"
      px={2}
      py="2px"
      borderRadius="full"
      fontSize="11px"
      fontWeight="700"
      whiteSpace="nowrap"
      style={{ background: suave, color: cor }}
    >
      {texto}
    </Box>
  );
}

export function Cartao({
  titulo,
  sub,
  children,
  acao,
}: {
  titulo: string;
  sub?: React.ReactNode;
  children?: React.ReactNode;
  acao?: React.ReactNode;
}) {
  return (
    <GlassCard intensity="medium" p={4}>
      <Flex justify="space-between" align="flex-start" gap={2}>
        <Box>
          <Text fontSize="14px" fontWeight="700" color={COR.tinta}>
            {titulo}
          </Text>
          {sub && (
            <Text fontSize="11.5px" color={COR.apagado} mb={3}>
              {sub}
            </Text>
          )}
        </Box>
        {acao}
      </Flex>
      {children}
    </GlassCard>
  );
}

export function Indicador({
  rotulo,
  valor,
  sub,
  onClick,
}: {
  rotulo: string;
  valor: React.ReactNode;
  sub?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <GlassCard
      intensity="medium"
      p={4}
      cursor={onClick ? 'pointer' : undefined}
      onClick={onClick}
      _hover={onClick ? { boxShadow: 'md' } : undefined}
    >
      <Text
        fontSize="10.5px"
        fontWeight="700"
        letterSpacing=".9px"
        color={COR.apagado}
        textTransform="uppercase"
      >
        {rotulo}
      </Text>
      <Text fontSize="22px" fontWeight="700" color={COR.tinta} lineHeight="1.2" mt={1}>
        {valor}
      </Text>
      {sub && (
        <Text fontSize="11.5px" color={COR.apagado} mt={1}>
          {sub}
        </Text>
      )}
    </GlassCard>
  );
}

export function Anel({
  valor,
  total,
  cor,
  rotulo,
}: {
  valor: number;
  total: number;
  cor: string;
  rotulo: string;
}) {
  return (
    <Flex align="center" gap={3}>
      <CircularProgress
        value={total ? (valor / total) * 100 : 0}
        color={cor}
        size="74px"
        thickness="9px"
      >
        <CircularProgressLabel fontSize="13px" fontWeight="700">
          {valor}/{total}
        </CircularProgressLabel>
      </CircularProgress>
      <Text fontSize="12.5px" color={COR.apagado}>
        {rotulo}
      </Text>
    </Flex>
  );
}

export function Vazio({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="12.5px" color={COR.apagado} py={4} textAlign="center">
      {children}
    </Text>
  );
}

export function Aviso({
  children,
  cor = COR.atencao,
}: {
  children: React.ReactNode;
  cor?: string;
}) {
  return (
    <Box
      fontSize="12.5px"
      color={COR.tinta}
      borderLeft="3px solid"
      borderRadius="6px"
      px={3}
      py={2}
      style={{ borderLeftColor: cor, background: 'rgba(15,23,42,0.03)' }}
    >
      {children}
    </Box>
  );
}

/** Abas internas de uma tela (ex.: DRE, balanço, fluxo de caixa). */
export function Abas<T extends string>({
  opcoes,
  atual,
  onMudar,
}: {
  opcoes: { chave: T; nome: string; qtd?: number }[];
  atual: T;
  onMudar: (chave: T) => void;
}) {
  return (
    <Flex gap={2} wrap="wrap" mb={3}>
      {opcoes.map(o => {
        const ligada = o.chave === atual;
        return (
          <Button
            key={o.chave}
            size="xs"
            borderRadius="full"
            variant="outline"
            aria-pressed={ligada}
            borderColor={ligada ? COR.andamento : COR.borda}
            bg={ligada ? COR.andamentoSuave : 'white'}
            color={ligada ? COR.andamento : COR.apagado}
            onClick={() => onMudar(o.chave)}
          >
            {o.nome}
            {o.qtd != null && (
              <Text as="span" ml={1} fontWeight="800">
                {o.qtd}
              </Text>
            )}
          </Button>
        );
      })}
    </Flex>
  );
}

/** Duas fontes do mesmo número, lado a lado, na mesma escala. */
/** Barras horizontais na mesma escala: rótulo, barra e valor por linha. */
export function Barras({
  linhas,
  formato = num,
}: {
  linhas: { rotulo: string; valor: number | null | undefined; cor: string; destaque?: boolean }[];
  formato?: (v: number | null | undefined) => string;
}) {
  const maior = Math.max(...linhas.map(l => Math.abs(l.valor || 0)), 1);
  return (
    <>
      {linhas.map(l => (
        <Flex key={l.rotulo} align="center" gap={2} my="3px">
          <Text
            fontSize="11.5px"
            color={l.destaque ? COR.tinta : COR.apagado}
            fontWeight={l.destaque ? '600' : undefined}
            w="38%"
            noOfLines={1}
            title={l.rotulo}
          >
            {l.rotulo}
          </Text>
          <Box flex={1} h="6px" bg={COR.neutroSuave} borderRadius="3px">
            <Box
              h="6px"
              borderRadius="3px"
              style={{ width: `${(Math.abs(l.valor || 0) / maior) * 100}%`, background: l.cor }}
            />
          </Box>
          <Text fontSize="12px" fontWeight="700" w="118px" textAlign="right">
            {formato(l.valor)}
          </Text>
        </Flex>
      ))}
    </>
  );
}

export function Compara({
  rotuloA,
  a,
  rotuloB,
  b,
  formato = num,
}: {
  rotuloA: string;
  a: number | null | undefined;
  rotuloB: string;
  b: number | null | undefined;
  formato?: (v: number | null | undefined) => string;
}) {
  return (
    <Barras
      formato={formato}
      linhas={[
        { rotulo: rotuloA, valor: a, cor: COR.andamento },
        { rotulo: rotuloB, valor: b, cor: '#0891B2' },
      ]}
    />
  );
}

/** Cascata: totais em azul, o que somou em verde e o que tirou em vermelho. */
export function Cascata({ passos }: { passos: IPasso[] }) {
  const barras = barrasDaCascata(passos);
  const corDe = (b: (typeof barras)[number]) => {
    if (b.total) return COR.andamento;
    return b.valor >= 0 ? COR.ok : COR.erro;
  };
  return (
    <Box h="270px">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={barras} margin={{ top: 8, right: 8, bottom: 28, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COR.borda} />
          <XAxis
            dataKey="rotulo"
            interval={0}
            angle={-18}
            textAnchor="end"
            tick={{ fontSize: 10.5 }}
          />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => numCurto(v)} width={74} />
          <GraficoTooltip
            formatter={(_faixa: unknown, _nome: unknown, item: { payload?: IPasso }) => [
              num(item.payload?.valor),
              item.payload?.rotulo ?? '',
            ]}
            labelFormatter={() => ''}
          />
          <Bar dataKey="faixa" radius={[3, 3, 3, 3]} isAnimationActive={false}>
            {barras.map(b => (
              <Cell key={b.rotulo} fill={corDe(b)} />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

/** Linha totalizadora que abre nas linhas que a compõem. */
export function LinhaQueAbre({
  celulas,
  filhas,
  forte,
}: {
  celulas: React.ReactNode;
  filhas?: React.ReactNode;
  forte?: boolean;
}) {
  const [aberta, setAberta] = React.useState(false);
  const abre = !!filhas;
  return (
    <>
      <Tr
        cursor={abre ? 'pointer' : undefined}
        fontWeight={forte ? 700 : undefined}
        bg={forte ? 'rgba(15,23,42,0.03)' : undefined}
        _hover={abre ? { bg: '#F5F9FD' } : undefined}
        onClick={abre ? () => setAberta(v => !v) : undefined}
      >
        <Td w="18px" px={1}>
          {abre && (aberta ? <CaretDown size={11} /> : <CaretRight size={11} />)}
        </Td>
        {celulas}
      </Tr>
      {aberta && filhas}
    </>
  );
}
